import React from 'react';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '@/components/ui/card';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Search, Eye, Send } from 'lucide-react';

const DashboardPage = ({
  campaigns = [],
  organizations = [],
  interactionFilterStatus,
  setInteractionFilterStatus,
  interactionSearchTerm,
  setInteractionSearchTerm,
  filteredContactInteractions = [],
  formatTimestamp,
  markAsRead,
  handleQuickReply,
  setSelectedInteraction,
  totalContactsUnique,
  totalContactsRows,
  sampleEmails,
}) => {
  const displayedInteractions = (filteredContactInteractions || []).filter((i) => {
    const isReplyType = (i.type || '').toLowerCase() === 'reply';
    const isReplied = (i.status || i.emailLogStatus || '').toLowerCase() === 'replied';
    const hasReply = Boolean((i.lastReplyText || '').trim()) || Boolean(i.lastReplyAt);
    return isReplyType || isReplied || hasReply;
  });

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold">Dashboard</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Total Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-5xl font-bold">{campaigns.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Active Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-5xl font-bold">
              {Array.isArray(campaigns) ? campaigns.filter(c => c.status === 'active').length : 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Contacts</CardTitle>
            <CardDescription>unique by email / total rows</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-5xl font-bold">
              {totalContactsUnique ?? 0}
              <span className="text-xl text-muted-foreground"> / {totalContactsRows ?? 0}</span>
            </div>

            {process.env.NODE_ENV !== 'production' && sampleEmails?.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground space-y-1">
                <div>sample emails:</div>
                {sampleEmails.map((s) => (
                  <div key={s.id}>#{s.id}: {String(s.email || '—')}</div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Recent Responses
            <Select value={interactionFilterStatus} onValueChange={setInteractionFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="viewed">Viewed (Unreplied)</SelectItem>
                <SelectItem value="awaiting_reply">Awaiting Reply</SelectItem>
                <SelectItem value="replied">Replied</SelectItem>
              </SelectContent>
            </Select>
          </CardTitle>
          <CardDescription>Latest interactions from your campaigns.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search responses..."
              className="pl-9"
              value={interactionSearchTerm}
              onChange={(e) => setInteractionSearchTerm(e.target.value)}
            />
          </div>

          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedInteractions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No recent responses found.
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedInteractions
                    .slice()
                    .sort((a, b) => {
                      const tA = new Date(a.lastReplyAt || a.timestamp || a.lastInteraction || 0).getTime();
                      const tB = new Date(b.lastReplyAt || b.timestamp || b.lastInteraction || 0).getTime();
                      const safeA = Number.isFinite(tA) ? tA : 0;
                      const safeB = Number.isFinite(tB) ? tB : 0;
                      return safeB - safeA;
                    })
                    .map((interaction) => (
                      <TableRow
                        key={interaction.id}
                        className={!interaction.read ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''}
                      >
                        <TableCell className="font-medium">{interaction.name}</TableCell>
                        <TableCell>{interaction.organizationName}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {interaction.lastReplyText || interaction.content || '—'}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(
                            interaction.lastReplyAt ||
                            interaction.timestamp ||
                            interaction.lastInteraction
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={interaction.status === 'Hot Lead' ? 'default' : 'secondary'}>
                            {interaction.status}
                          </Badge>
                          {!interaction.read && <Badge className="ml-2" variant="destructive">New</Badge>}
                          {interaction.read && !interaction.replied && interaction.type === 'reply' && (
                            <Badge className="ml-2" variant="outline">Awaiting Reply</Badge>
                          )}
                          {interaction.replied && (
                            <Badge className="ml-2" variant="success">Replied</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedInteraction(interaction);
                              markAsRead(interaction.id);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {interaction.type === 'reply' && !interaction.replied && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleQuickReply(interaction)}
                              title="Quick Reply"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;